import React, { useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  HomeIcon,
  BuildingOfficeIcon,
  ChartBarIcon,
  ClipboardDocumentListIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  GiftIcon,
  ShoppingCartIcon,
  BanknotesIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline';
import AgentBottomNav from './AgentBottomNav';

const AgentLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navigation = [
    { name: 'דשבורד', href: '/agent', icon: HomeIcon, exact: true },
    { name: 'יצירת הזמנה מהירה', href: '/agent/products', icon: ShoppingCartIcon },
    { name: 'החברות שלי', href: '/agent/companies', icon: BuildingOfficeIcon },
    { name: 'תשלומים וחובות', href: '/agent/payments', icon: BanknotesIcon },
    { name: 'צ\'קים', href: '/agent/checks', icon: DocumentTextIcon },
    { name: 'החזרות', href: '/agent/returns', icon: ArrowPathIcon },
    { name: 'דיווח בלאי', href: '/agent/waste-report', icon: ExclamationTriangleIcon },
    { name: 'היעדים שלי', href: '/agent/targets', icon: ChartBarIcon },
    { name: 'ההזמנות שלי', href: '/agent/orders', icon: ClipboardDocumentListIcon },
    { name: 'חבילות ומבצעים', href: '/agent/bundles-discounts', icon: GiftIcon },
    { name: 'הפרופיל שלי', href: '/agent/profile', icon: UserCircleIcon }
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.href;
    }
    return location.pathname.startsWith(item.href);
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Mobile sidebar */}
      <div className={`fixed inset-y-0 right-0 w-64 bg-white border-l border-gray-200 transform transition-transform duration-300 ease-in-out z-50 lg:hidden overflow-y-auto ${sidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        {/* Header - Sticky top */}
        <div className="sticky top-0 z-10 flex items-center justify-between h-14 px-4 border-b border-gray-200 bg-white flex-shrink-0">
          <img 
            src="/RamiCareLogo-02.png" 
            alt="Rami-Care Logo" 
            className="h-8 w-auto object-contain"
          />
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-1.5 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="px-3 py-2 space-y-0.5">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className={`ml-2 h-5 w-5 flex-shrink-0 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        {/* User section - Sticky bottom */}
        <div className="sticky bottom-0 z-10 border-t border-gray-200 p-3 bg-white mt-4">
          <div className="flex items-center mb-2">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-semibold text-xs">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
            </div>
            <div className="mr-2 flex-1 min-w-0">
              <p className="text-xs font-medium text-gray-900 truncate">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-[10px] text-gray-500">סוכן מכירות</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center justify-center w-full px-3 py-2 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-4 w-4 ml-2" />
            התנתק
          </button>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:right-0 lg:flex lg:w-64 lg:flex-col lg:border-l lg:border-gray-200 lg:bg-white z-50">
        <div className="flex items-center justify-center h-20 px-6 border-b border-gray-200 bg-gradient-to-br from-green-50 to-white">
          <img 
            src="/RamiCareLogo-02.png" 
            alt="Rami-Care Logo" 
            className="h-14 w-auto object-contain"
          />
        </div>

        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                  active
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <Icon className={`ml-3 h-6 w-6 flex-shrink-0 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-gray-200 p-4">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-700 font-semibold text-lg">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </span>
              </div>
            </div>
            <div className="mr-3">
              <p className="text-sm font-medium text-gray-900">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-gray-500">סוכן מכירות</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-4 py-2 text-sm font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
          >
            <ArrowRightOnRectangleIcon className="h-5 w-5 ml-2" />
            התנתק
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pr-64 relative z-0">
        {/* Mobile header */}
        <div className="sticky top-0 z-10 lg:hidden bg-gradient-to-br from-green-50 to-white border-b border-gray-200">
          <div className="flex items-center justify-between h-16 px-4">
            <img 
              src="/RamiCareLogo-02.png" 
              alt="Rami-Care Logo" 
              className="h-10 w-auto object-contain"
            />
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-gray-500 hover:text-gray-600 hover:bg-gray-100"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="py-6 pb-24 md:pb-6">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Outlet />
          </div>
        </main>
      </div>

      {/* Bottom Navigation for Tablets/Mobile */}
      <AgentBottomNav />
    </div>
  );
};

export default AgentLayout;

